import React, { useMemo } from "react";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import { formatCurrency } from "../../utils/formatters";

/**
 * PricingTierTable - Displays volume/MOQ pricing tiers for a product
 *
 * @param {object} product - Product object with pricing data
 * @param {number} currentQuantity - Current selected quantity (to highlight active tier)
 * @param {boolean} compact - Use condensed display for product cards
 * @param {boolean} showHeader - Show the "Volume Pricing" header
 * @param {string} className - Additional CSS classes
 */
const PricingTierTable = ({
  product,
  currentQuantity = 0,
  compact = false,
  showHeader = true,
  className = "",
}) => {
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.unitPrice, product?.discountUnitPrice, product?.productSkuVariantMOQPricing]);

  // Get pricing tiers
  const tiers = useMemo(() => {
    if (!calculator) return [];
    return calculator.getMOQPricingTiers();
  }, [calculator]);

  // Get active tier based on current quantity
  const activeTier = useMemo(() => {
    if (!calculator || currentQuantity <= 0) return null;
    return calculator.getActiveTier(currentQuantity);
  }, [calculator, currentQuantity]);

  // Don't render if no MOQ pricing and no discount
  const hasSpecialPricing = calculator?.hasMOQPricing() || calculator?.hasDiscountPrice();
  if (!hasSpecialPricing || tiers.length === 0) {
    return null;
  }

  // Compact view for product cards - just show badge and min savings
  if (compact) {
    const maxSavings = Math.max(...tiers.map(t => t.savingsPercent));
    if (maxSavings <= 0) return null;

    return (
      <div className={`pricing-tier-badge ${className}`}>
        <span className="badge bg-success">
          <i className="bi bi-tag-fill me-1"></i>
          Save up to {maxSavings}%
        </span>
      </div>
    );
  }

  return (
    <div className={`pricing-tier-table ${className}`}>
      {showHeader && (
        <div className="pricing-tier-table__header">
          <i className="bi bi-graph-down-arrow me-2"></i>
          Volume Pricing
        </div>
      )}
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            <th>Quantity</th>
            <th>Unit Price</th>
            <th>Savings</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier) => {
            const isActive = activeTier?.id === tier.id && currentQuantity > 0;
            return (
              <tr
                key={tier.id}
                className={`pricing-tier-row ${isActive ? "pricing-tier-row--active" : ""}`}
              >
                <td>
                  <span className="tier-quantity">
                    {tier.label}
                    {tier.isBase && <span className="text-muted ms-1">(Base)</span>}
                  </span>
                </td>
                <td>
                  <span className="tier-price">
                    {formatCurrency(tier.unitPrice)}
                    {tier.unitPrice < tier.originalPrice && !tier.isBase && (
                      <small className="text-decoration-line-through text-muted ms-2">
                        {formatCurrency(tier.originalPrice)}
                      </small>
                    )}
                  </span>
                </td>
                <td>
                  {tier.savingsPercent > 0 ? (
                    <span className="tier-savings text-success">
                      {tier.savingsPercent}% off
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {activeTier && currentQuantity > 0 && activeTier.savingsPercent > 0 && (
        <div className="pricing-tier-table__active-savings">
          <i className="bi bi-check-circle-fill me-1"></i>
          You're saving {activeTier.savingsPercent}% with {activeTier.label} pricing!
        </div>
      )}
    </div>
  );
};

/**
 * PricingBadge - Compact indicator that volume pricing is available
 */
export const PricingBadge = ({ product, className = "" }) => {
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.productSkuVariantMOQPricing]);

  if (!calculator?.hasMOQPricing()) {
    return null;
  }

  const tiers = calculator.getMOQPricingTiers();
  const maxSavings = Math.max(...tiers.map(t => t.savingsPercent));

  if (maxSavings <= 0) return null;

  return (
    <span className={`badge bg-success ${className}`}>
      <i className="bi bi-graph-down-arrow me-1"></i>
      Volume Pricing
    </span>
  );
};

/**
 * DiscountPriceBadge - Shows when a product has a discount price
 */
export const DiscountPriceBadge = ({ product, className = "" }) => {
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.unitPrice, product?.discountUnitPrice]);

  if (!calculator?.hasDiscountPrice()) {
    return null;
  }

  const originalPrice = calculator.getOriginalUnitPrice();
  const discountPrice = calculator.getBaseUnitPrice();
  const savingsPercent = Math.round((1 - discountPrice / originalPrice) * 100);

  return (
    <span className={`badge bg-danger ${className}`}>
      <i className="bi bi-tag-fill me-1"></i>
      {savingsPercent}% Off
    </span>
  );
};

/**
 * InlinePriceDisplay - Shows original and discount price inline
 */
export const InlinePriceDisplay = ({
  product,
  showPerUnit = true,
  className = "",
}) => {
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.unitPrice, product?.discountUnitPrice]);

  if (!calculator) return null;

  const hasDiscount = calculator.hasDiscountPrice();
  const originalPrice = calculator.getOriginalUnitPrice();
  const currentPrice = calculator.getBaseUnitPrice();

  return (
    <span className={`inline-price-display ${className}`}>
      {hasDiscount && (
        <span className="price-original text-decoration-line-through text-muted me-2">
          {formatCurrency(originalPrice)}
        </span>
      )}
      <span className={`price-current ${hasDiscount ? "text-danger fw-bold" : ""}`}>
        {formatCurrency(currentPrice)}
      </span>
      {showPerUnit && <span className="text-muted">/unit</span>}
    </span>
  );
};

export default PricingTierTable;
